import get from 'lodash/get';
import { getEndpoints } from 'selectors/endpointsModal';
import { ENDPOINT_CONNECT_STATUSES as connectStatuses } from 'xm-constants/endpointConnectionStatuses';
import { waitForConnectStatusChange } from 'utils/endpointConnections';
import { call } from '../xmAPI';

export const ENDPOINTS_MODAL_HIDE = 'ENDPOINTS_MODAL_HIDE';
export const ENDPOINTS_MODAL_SHOW = 'ENDPOINTS_MODAL_SHOW';

export const FETCH_PLAN_ENDPOINTS = 'FETCH_PLAN_ENDPOINTS';
export const FETCH_PLAN_ENDPOINTS_START = `${FETCH_PLAN_ENDPOINTS}_START`;
export const FETCH_PLAN_ENDPOINTS_SUCCESS = `${FETCH_PLAN_ENDPOINTS}_SUCCESS`;
export const FETCH_PLAN_ENDPOINTS_FAIL = `${FETCH_PLAN_ENDPOINTS}_FAIL`;

export const ENDPOINT_CREATE = 'ENDPOINT_CREATE'; // Synchronous action, so no "SUCCESS"

export const ENDPOINT_DELETE = 'ENDPOINT_DELETE';
export const ENDPOINT_DELETE_START = 'ENDPOINT_DELETE_START';
export const ENDPOINT_DELETE_SUCCESS = `${ENDPOINT_DELETE}_SUCCESS`;

export const ENDPOINT_SAVE = 'ENDPOINT_SAVE';
export const ENDPOINT_SAVE_START = `${ENDPOINT_SAVE}_START`;
export const ENDPOINT_SAVE_SUCCESS = `${ENDPOINT_SAVE}_SUCCESS`;

export const SINGLE_ENDPOINT_LOAD = 'SINGLE_ENDPOINT_LOAD';
export const SINGLE_ENDPOINT_LOAD_START = `${SINGLE_ENDPOINT_LOAD}_START`;
export const SINGLE_ENDPOINT_LOAD_SUCCESS = `${SINGLE_ENDPOINT_LOAD}_SUCCESS`;

const LOAD_USAGE_IN_BACKGROUND = 'LOAD_USAGE_IN_BACKGROUND';
export const LOAD_USAGE_IN_BACKGROUND_SUCCESS = `${LOAD_USAGE_IN_BACKGROUND}_SUCCESS`;
export const LOAD_USAGE_IN_BACKGROUND_START = `${LOAD_USAGE_IN_BACKGROUND}_START`;

export const VERIFY_CREDENTIALS = `ENDPOINTS_MODAL_VERIFY_CREDENTIALS`;
export const ENDPOINT_OAUTH_CONNECT = `ENDPOINT_OAUTH_CONNECT`;

// Used in GWT as a crutch for loading the assignee for XMATTERS typed endpoints until xm-api supports this:
export const GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_START =
  'GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_START';
export const GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_SUCCESS =
  'GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_SUCCESS';
export const GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_FAIL =
  'GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_FAIL';
// gwtEndpointGetXmattersAssigneeSuccess is a quick patch to render the new assignee without refetching API data:
export const gwtEndpointGetXmattersAssigneeSuccess = assignee => ({
  type: GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_SUCCESS,
  payload: assignee,
});

export const ROOT_URL = '/webapi/xm/1/plans';

export const credsVerifiedEnum = {
  NOT_VERIFIED: 'NOT_VERIFIED',
  PENDING: 'PENDING',
  VERIFICATION_SUCCESS: 'VERIFICATION_SUCCESS',
  VERIFICATION_FAILURE: 'VERIFICATION_FAILURE',
};

// When authType is not specified, the Endpoint Type dropdown doesn't have any type restriction.
export const create = (authType, defaultName = '') => ({
  type: ENDPOINT_CREATE,
  payload: {
    authType,
    defaultName, // This is not hardcoded because of French translation
  },
});

export const getPlanId = state =>
  state.currentCommunicationPlan.planId ||
  // Because of how integrationConfigs' initial state is set up, oneConfig.data can be null. Guard it with get.
  get(state, 'integrationConfigs.oneConfig.data.id', null);

// Fetch the list of endpoints.
export function fetchPlanEndpoints(planId) {
  return {
    type: FETCH_PLAN_ENDPOINTS,
    meta: {
      xmApi: true,
      uri: `${ROOT_URL}/${planId}/endpoints`,
      method: 'getAll',
    },
  };
}

export const hideModal = () => ({ type: ENDPOINTS_MODAL_HIDE });

/**
 * Load a single endpoint by ID.
 * @param {string} endpointId
 * @param {boolean} loadUsageInBackground If true, will only load it without selecting it in the endpoints modal.
 *    Useful when deleting an endpoint without selecting it first.
 */
export const loadEndpoint =
  (endpointId, loadUsageInBackground = false) =>
  (dispatch, getState) => {
    const planId = getPlanId(getState());
    return dispatch({
      type: loadUsageInBackground
        ? LOAD_USAGE_IN_BACKGROUND
        : SINGLE_ENDPOINT_LOAD,
      meta: {
        xmApi: true,

        // Using ?embed=usage to pull usages. Not available when pulling the whole list.
        uri: `${ROOT_URL}/${planId}/endpoints/${endpointId}?embed=usage`,
        method: 'get',
      },
    });
  };

export const remove = (endpointId, nextId) => (dispatch, getState) => {
  const planId = getPlanId(getState());

  return dispatch({
    type: ENDPOINT_DELETE,
    meta: {
      xmWebApp: true,
      uri: `${ROOT_URL}/${planId}/endpoints/${endpointId}`,
      method: 'del',
    },
  }).then(() => {
    if (nextId) {
      dispatch(loadEndpoint(nextId));
    }
  });
};

// Save a single endpoint to the server.
export const save = payload => (dispatch, getState) => {
  const planId = getPlanId(getState());

  return dispatch({
    type: ENDPOINT_SAVE,
    meta: {
      xmWebApp: true,
      uri: `${ROOT_URL}/${planId}/endpoints`,
      method: 'post',
      params: payload,
    },
  });
};

// Only opens the dialog if successfully loaded endpoints.
export const showModal = authTypeFilter => (dispatch, getState) => {
  const planId = getPlanId(getState());

  return dispatch(fetchPlanEndpoints(planId))
    .then(() =>
      dispatch({
        type: ENDPOINTS_MODAL_SHOW,
        payload: { authTypeFilter },
      }),
    )
    .then(() => {
      if (getEndpoints(getState()).length === 0) {
        dispatch(create(authTypeFilter));
      }
    })
    .catch(() =>
      // Errors would have been thrown in the previous chains, so no need to throw again.
      // eslint-disable-next-line
      console.error('Cannot show the endpoints modal due to errors.'),
    );
};

export const credentialsVerified = (
  verified = credsVerifiedEnum.NOT_VERIFIED,
  message = '',
) => ({
  message,
  type: VERIFY_CREDENTIALS,
  verified,
});

export const verifyCreds = payload => dispatch => {
  const data = JSON.stringify(payload);
  dispatch(credentialsVerified(credsVerifiedEnum.PENDING));

  return call('/rest/flow-endpoints/credentials-validation', {
    method: 'POST',
    body: data,
  })
    .then(result => {
      const { message, success } = result;
      // The endpoint will return result.success == true if we're good.
      return dispatch(
        credentialsVerified(
          success
            ? credsVerifiedEnum.VERIFICATION_SUCCESS
            : credsVerifiedEnum.VERIFICATION_FAILURE,
          message,
        ),
      );
    })
    .catch(() => {
      dispatch(credentialsVerified(credsVerifiedEnum.VERIFICATION_FAILURE));
    });
};

export const updateConnectStatus = (id, status) => ({
  type: ENDPOINT_OAUTH_CONNECT,
  id,
  status,
});

export const validateConnect = (id, tokenUrl) => dispatch => {
  dispatch(updateConnectStatus(id, connectStatuses.CONNECTING));
  window.open(tokenUrl, '_blank');
  waitForConnectStatusChange(id)
    .then(() => dispatch(updateConnectStatus(id, connectStatuses.CONNECTED)))
    .catch(() => dispatch(updateConnectStatus(id, connectStatuses.FAILED)));
};
